import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, Users, Target, PhoneCall } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';

const AboutUs = () => {
  usePageMeta('About Alpha Epoxy Floors Sydney | 15+ Years of Excellence', 'Learn about Alpha Epoxy Floors — Sydney\'s trusted epoxy flooring specialists since 2010. 500+ projects, 98% satisfaction. Quality materials, expert craftsmanship, comprehensive warranty.');
  return (
    <div className="min-h-screen bg-black">
      <Header activePage="about" />

      <div className="pt-28">
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="/images/about-hero.jpg" 
              alt="About Alpha Epoxy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-block px-6 py-2 bg-red-600 text-white rounded-full text-sm font-bold mb-6 animate-pulse" data-testid="about-hero-badge">
                Since 2010 - 15+ Years of Excellence
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" data-testid="about-hero-title">
                About Alpha Epoxy Flooring
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                Sydney's most trusted name in premium epoxy flooring solutions. 
                Building lasting relationships through exceptional quality and service.
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-red-600/10 backdrop-blur-sm rounded-xl border border-red-700/20">
                  <div className="text-4xl font-bold text-red-600 mb-2">500+</div>
                  <div className="text-gray-300 text-sm">Projects</div>
                </div>
                <div className="text-center p-4 bg-red-600/10 backdrop-blur-sm rounded-xl border border-red-700/20">
                  <div className="text-4xl font-bold text-red-600 mb-2">98%</div>
                  <div className="text-gray-300 text-sm">Satisfaction</div>
                </div>
                <div className="text-center p-4 bg-red-600/10 backdrop-blur-sm rounded-xl border border-red-700/20">
                  <div className="text-4xl font-bold text-red-600 mb-2">15+</div>
                  <div className="text-gray-300 text-sm">Years</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-24 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
                <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                  <p>Founded in 2010, Alpha Epoxy Flooring began with a simple mission: to provide Sydney homeowners and businesses with the highest quality epoxy flooring solutions backed by exceptional customer service.</p>
                  <p>Over the past 15+ years, we've grown from a small local operation to become one of Sydney's most trusted epoxy flooring specialists, completing over 500 projects and earning a 98% customer satisfaction rating.</p>
                  <p>Our success is built on three pillars: quality materials, expert craftsmanship, and unwavering commitment to customer satisfaction. Every project, whether residential or commercial, receives the same attention to detail and professional excellence.</p>
                </div>
              </div>
              <div>
                <img src="/images/about-hero.jpg" alt="About Alpha Epoxy" className="rounded-xl shadow-2xl" />
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Core Values</h2>
              <p className="text-xl text-gray-600">What drives us to deliver excellence every day</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: <Shield className="w-8 h-8 text-white" />, title: "Quality First", desc: "We use only premium, industrial-grade materials and proven application techniques" },
                { icon: <Award className="w-8 h-8 text-white" />, title: "Expert Craftsmanship", desc: "Our team consists of certified, experienced professionals with 15+ years expertise" },
                { icon: <Users className="w-8 h-8 text-white" />, title: "Customer Focused", desc: "Your satisfaction is our priority. We listen, advise, and deliver beyond expectations" },
                { icon: <Target className="w-8 h-8 text-white" />, title: "Attention to Detail", desc: "From prep to finish, we ensure every aspect meets our high standards" }
              ].map((value, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-red-700/30">
                      {value.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-24 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Why Sydney Trusts Alpha Epoxy</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { title: "15+ Years of Excellence", desc: "Over a decade and a half of experience has taught us what works and what doesn't. We've perfected our processes, refined our techniques, and built relationships with the best suppliers to ensure every project succeeds." },
                { title: "500+ Happy Customers", desc: "From residential garages to large commercial warehouses, we've completed hundreds of successful projects. Our portfolio speaks to our versatility and commitment to excellence across all applications." },
                { title: "Premium Materials", desc: "We partner with leading Australian and international suppliers to source the highest quality epoxy systems. Our materials are UV-stable, chemical-resistant, and designed for the harsh Australian climate." },
                { title: "Comprehensive Warranty", desc: "We stand behind our work with comprehensive warranty coverage. If any issues arise, we'll make it right. Your investment is protected, and your satisfaction is guaranteed." }
              ].map((item, i) => (
                <Card key={i} className="border-none shadow-lg"><CardContent className="p-8"><h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3><p className="text-gray-600 leading-relaxed">{item.desc}</p></CardContent></Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24" style={{ backgroundColor: '#1C2541' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Transform Your Space?</h2>
            <p className="text-xl text-gray-300 mb-8">Join hundreds of satisfied Sydney customers who chose Alpha Epoxy for their flooring needs</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-12 py-6 text-xl font-bold">
                <PhoneCall className="w-6 h-6 mr-3" />Get Free Quote
              </Button>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-12 py-6 text-xl font-bold">Contact Us</Button>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default AboutUs;
